import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useKitchenSpecialties,
  useCreateKitchenSpecialty,
  useUpdateKitchenSpecialty,
  useDeleteKitchenSpecialty,
} from '@/hooks/useKitchenSpecialties'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { AlertModal } from '@/components/ui/alert-modal'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Pencil, Trash2, Plus, Save, X, ChefHat, Volume2 } from 'lucide-react'
import type { KitchenSpecialty } from '@/types/entities'

interface KitchenSpecialtiesSectionProps {
  restaurantId: string
}

const DEFAULT_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#f97316', // Orange
  '#22c55e', // Green
  '#06b6d4', // Cyan
  '#f59e0b', // Amber
  '#ef4444', // Red
]

const NOTIFICATION_SOUNDS = [
  { value: 'notif1.mp3', label: 'Notification 1' },
  { value: 'notif2.mp3', label: 'Notification 2' },
  { value: 'notif3.mp3', label: 'Notification 3' },
  { value: 'notif4.mp3', label: 'Notification 4' },
  { value: 'notif5.mp3', label: 'Notification 5' },
]

export function KitchenSpecialtiesSection({ restaurantId }: KitchenSpecialtiesSectionProps) {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'

  const { data: specialties, isLoading } = useKitchenSpecialties(restaurantId)
  const createSpecialty = useCreateKitchenSpecialty(restaurantId)
  const updateSpecialty = useUpdateKitchenSpecialty(restaurantId)
  const deleteSpecialty = useDeleteKitchenSpecialty(restaurantId)

  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState({
    nameAr: '',
    nameFr: '',
    color: DEFAULT_COLORS[0],
    isActive: true,
    notificationSound: 'notif1.mp3',
  })

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    title: string
    description: string
    onConfirm: () => void
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  })

  // Alert dialog state
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean
    title: string
    description: string
  }>({
    open: false,
    title: '',
    description: '',
  })

  const resetForm = () => {
    setForm({
      nameAr: '',
      nameFr: '',
      color: DEFAULT_COLORS[0],
      isActive: true,
      notificationSound: 'notif1.mp3',
    })
  }

  const playNotificationSound = (soundFile: string) => {
    try {
      const audio = new Audio(`/notifications/${soundFile}`)
      audio.play().catch((err) => console.error('Failed to play sound:', err))
    } catch (error) {
      console.error('Failed to play notification sound:', error)
    }
  }

  const handleAdd = async () => {
    // Require at least one language
    if (!form.nameAr && !form.nameFr) return

    // Auto-fill missing language with the other
    const nameAr = form.nameAr || form.nameFr
    const nameFr = form.nameFr || form.nameAr

    await createSpecialty.mutateAsync({
      nameAr,
      nameFr,
      color: form.color,
      notificationSound: form.notificationSound,
    } as { nameAr: string; nameFr: string; color: string; notificationSound: string })

    resetForm()
    setIsAdding(false)
  }

  const handleEdit = (specialty: KitchenSpecialty) => {
    setEditingId(specialty.id)
    setForm({
      nameAr: specialty.nameAr,
      nameFr: specialty.nameFr,
      color: specialty.color,
      isActive: specialty.isActive,
      notificationSound: specialty.notificationSound || 'notif1.mp3',
    })
  }

  const handleSave = async (specialtyId: string) => {
    // Require at least one language
    if (!form.nameAr && !form.nameFr) return

    // Auto-fill missing language with the other
    const nameAr = form.nameAr || form.nameFr
    const nameFr = form.nameFr || form.nameAr

    await updateSpecialty.mutateAsync({
      specialtyId,
      nameAr,
      nameFr,
      color: form.color,
      isActive: form.isActive,
      notificationSound: form.notificationSound,
    } as { specialtyId: string; nameAr: string; nameFr: string; color: string; isActive: boolean; notificationSound: string })

    resetForm()
    setEditingId(null)
  }

  const handleCancel = () => {
    resetForm()
    setEditingId(null)
    setIsAdding(false)
  }

  const handleDelete = (specialtyId: string) => {
    setConfirmDialog({
      open: true,
      title: t('settings.confirmDeleteSpecialty') || 'Delete Specialty',
      description: t('settings.confirmDeleteSpecialtyDescription') || 'Are you sure you want to delete this kitchen specialty? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteSpecialty.mutateAsync(specialtyId)
        } catch (error: any) {
          if (error?.message === 'SPECIALTY_IN_USE') {
            setAlertDialog({
              open: true,
              title: t('settings.specialtyInUseTitle') || 'Specialty In Use',
              description: t('settings.specialtyInUseDescription') || 'This specialty cannot be deleted because it is still being used. Please remove all references first.',
            })
          } else {
            setAlertDialog({
              open: true,
              title: t('common.error') || 'Error',
              description: error?.message || t('settings.deleteSpecialtyError') || 'Failed to delete specialty. Please try again.',
            })
          }
        }
      },
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('settings.kitchenSpecialties')}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {t('settings.kitchenSpecialtiesDescription')}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAdding(true)}
            disabled={isAdding}
          >
            <Plus className="w-4 h-4 me-2" />
            <span className="truncate">{t('settings.addSpecialty')}</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-4">
            {/* Add Form */}
            {isAdding && (
              <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20">
                      <Plus className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg">{t('settings.addSpecialty')}</h3>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>
                        {t('settings.specialtyNameAr')}
                        {!form.nameFr && <span className="text-destructive"> *</span>}
                      </Label>
                      <Input
                        value={form.nameAr}
                        onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
                        placeholder="مشروبات"
                        dir="rtl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        {t('settings.specialtyNameFr')}
                        {!form.nameAr && <span className="text-destructive"> *</span>}
                      </Label>
                      <Input
                        value={form.nameFr}
                        onChange={(e) => setForm({ ...form, nameFr: e.target.value })}
                        placeholder="Boissons"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('settings.oneLanguageRequired') || 'At least one language is required'}
                  </p>
                  <div className="mt-4 space-y-2">
                    <Label>{t('settings.specialtyColor')}</Label>
                    <div className="flex gap-2 flex-wrap">
                      {DEFAULT_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setForm({ ...form, color })}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            form.color === color
                              ? 'border-foreground scale-110'
                              : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <Label className="flex items-center gap-2">
                      <Volume2 className="w-4 h-4" />
                      {t('settings.notificationSound') || 'Notification Sound'}
                    </Label>
                    <div className="space-y-2">
                      {NOTIFICATION_SOUNDS.map((sound) => (
                        <label
                          key={sound.value}
                          className="flex items-center gap-2 p-2 rounded-md border cursor-pointer hover:bg-accent transition-colors"
                        >
                          <input
                            type="radio"
                            name="notificationSound"
                            value={sound.value}
                            checked={form.notificationSound === sound.value}
                            onChange={(e) => {
                              setForm({ ...form, notificationSound: e.target.value })
                              playNotificationSound(e.target.value)
                            }}
                            className="w-4 h-4 text-primary focus:ring-primary"
                          />
                          <span className="text-sm">{sound.label}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              const audio = new Audio(`/notifications/${sound.value}`)
                              audio.play().catch((err) => console.error('Failed to play sound:', err))
                            }}
                            className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                          >
                            {t('settings.preview') || 'Preview'}
                          </button>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end mt-4">
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      disabled={createSpecialty.isPending}
                    >
                      <X className="w-4 h-4 me-2" />
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleAdd}
                      disabled={createSpecialty.isPending || (!form.nameAr && !form.nameFr)}
                    >
                      <Save className="w-4 h-4 sm:me-2" />
                      <span className="hidden sm:inline">{t('common.save')}</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Specialties List */}
            {!specialties || specialties.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex justify-center mb-4">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
                    <ChefHat className="w-8 h-8 text-muted-foreground" />
                  </div>
                </div>
                <p className="text-muted-foreground">
                  {t('settings.noSpecialties')}
                </p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                {specialties.map((specialty) => (
                  <Card key={specialty.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      {editingId === specialty.id ? (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>
                              {t('settings.specialtyNameAr')}
                              {!form.nameFr && <span className="text-destructive"> *</span>}
                            </Label>
                            <Input
                              value={form.nameAr}
                              onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
                              dir="rtl"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>
                              {t('settings.specialtyNameFr')}
                              {!form.nameAr && <span className="text-destructive"> *</span>}
                            </Label>
                            <Input
                              value={form.nameFr}
                              onChange={(e) => setForm({ ...form, nameFr: e.target.value })}
                            />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t('settings.oneLanguageRequired') || 'At least one language is required'}
                          </p>
                          <div className="space-y-2">
                            <Label>{t('settings.specialtyColor')}</Label>
                            <div className="flex gap-2 flex-wrap">
                              {DEFAULT_COLORS.map((color) => (
                                <button
                                  key={color}
                                  type="button"
                                  onClick={() => setForm({ ...form, color })}
                                  className={`w-6 h-6 rounded-full border-2 transition-all ${
                                    form.color === color
                                      ? 'border-foreground scale-110'
                                      : 'border-transparent hover:scale-105'
                                  }`}
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <Volume2 className="w-4 h-4" />
                              {t('settings.notificationSound') || 'Notification Sound'}
                            </Label>
                            <div className="space-y-2">
                              {NOTIFICATION_SOUNDS.map((sound) => (
                                <label
                                  key={sound.value}
                                  className="flex items-center gap-2 p-2 rounded-md border cursor-pointer hover:bg-accent transition-colors"
                                >
                                  <input
                                    type="radio"
                                    name={`notificationSound-${specialty.id}`}
                                    value={sound.value}
                                    checked={form.notificationSound === sound.value}
                                    onChange={(e) => {
                                      setForm({ ...form, notificationSound: e.target.value })
                                      playNotificationSound(e.target.value)
                                    }}
                                    className="w-4 h-4 text-primary focus:ring-primary"
                                  />
                                  <span className="text-sm">{sound.label}</span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      const audio = new Audio(`/notifications/${sound.value}`)
                                      audio.play().catch((err) => console.error('Failed to play sound:', err))
                                    }}
                                    className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                                  >
                                    {t('settings.preview') || 'Preview'}
                                  </button>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <Label>{t('settings.specialtyActive')}</Label>
                            <Switch
                              checked={form.isActive}
                              onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSave(specialty.id)}
                              disabled={updateSpecialty.isPending || (!form.nameAr && !form.nameFr)}
                              className="flex-1"
                            >
                              <Save className="w-4 h-4 sm:me-2" />
                              <span className="hidden sm:inline">{t('common.save')}</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancel}
                              disabled={updateSpecialty.isPending}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-10 h-10 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: specialty.color + '20' }}
                              >
                                <ChefHat
                                  className="w-5 h-5"
                                  style={{ color: specialty.color }}
                                />
                              </div>
                              <div>
                                <div className="font-semibold">
                                  {isArabic ? specialty.nameAr : specialty.nameFr}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {isArabic ? specialty.nameFr : specialty.nameAr}
                                </div>
                              </div>
                            </div>
                            {!specialty.isActive && (
                              <span className="text-xs bg-muted px-2 py-1 rounded">
                                {t('common.inactive')}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 pt-2 border-t">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(specialty)}
                              className="flex-1"
                            >
                              <Pencil className="w-4 h-4 sm:me-2" />
                              <span className="hidden sm:inline">{t('common.edit')}</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(specialty.id)}
                              disabled={deleteSpecialty.isPending}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Confirm Dialog */}
      <ConfirmModal
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={t('common.delete') || 'Delete'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        variant="destructive"
      />

      {/* Alert Dialog */}
      <AlertModal
        open={alertDialog.open}
        onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
        title={alertDialog.title}
        description={alertDialog.description}
        actionLabel={t('common.ok') || 'OK'}
      />
    </Card>
  )
}
